import React, { useEffect, useState } from 'react';
import { App, Modal, Button, Space, Typography, Radio, Select } from 'antd';
import { CopyOutlined, CheckOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { shareFile, ShareResult } from '@/api/share';
import { useTranslation } from 'react-i18next';

const { Text, Paragraph } = Typography;

interface ShareDialogProps {
    visible: boolean;
    filePath: string;
    onClose: () => void;
    onSuccess?: () => void;
}

const ShareDialog: React.FC<ShareDialogProps> = ({ visible, filePath, onClose, onSuccess }) => {
    const { message } = App.useApp();
    const [selectedUsernames, setSelectedUsernames] = useState<string[]>([]);
    const [shareUrl, setShareUrl] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);
    const [permissionsType, setPermissionsType] = useState<'read' | 'write'>('read');
    const [shareResults, setShareResults] = useState<ShareResult[]>([]);
    const { t } = useTranslation('common');

    useEffect(() => {
        if (visible) {
            setSelectedUsernames([]);
            setShareUrl('');
            setShareResults([]);
        }
    }, [visible]);

    const handleShare = async () => {
        if (selectedUsernames.length === 0) {
            message.warning(t('share_dialog.select_user_warn'));
            return;
        }

        setSubmitting(true);
        try {
            const response = await shareFile({
                file_path: filePath,
                target_users: selectedUsernames,
                permission: permissionsType
            });
            const resData = response?.data;
            if (response?.ok && resData?.results) {
                const allSuccessfulSharesUpdated = resData.success_count > 0 && resData.updated_count === resData.success_count;

                if (allSuccessfulSharesUpdated && !resData.fail_count) {
                    setShareResults([]);
                    setShareUrl('');
                    message.success(t('share_dialog.share_updated'));
                    onSuccess?.();
                    return;
                }

                setShareResults(resData.results);

                if (resData?.link_code && !allSuccessfulSharesUpdated) {
                    setShareUrl(resData.link_code ? `${window.location.origin}/share/accept/${resData.link_code}` : '');
                }


                if (!!resData?.success_count) {

                    if (!resData.fail_count) {
                        message.success(t('share_dialog.all_success'));
                    } else {
                        message.warning(t('share_dialog.partial_success', { success: resData.success_count, fail: resData.fail_count }));
                    }
                    onSuccess?.();
                } else {
                    // All failed - don't show link
                    message.error(t('share_dialog.all_failed'));
                }
            } else {
                message.error(response?.message ?? t('share_dialog.link_failed'));
            }

        } catch (e) {
            console.error('Failed to share file', e);
            message.error(t('share_dialog.link_failed'));
        } finally {
            setSubmitting(false);
        }
    };

    const fileName = filePath.split('/').pop() || filePath;

    return (
        <Modal
            title={t('share_dialog.title', { name: fileName })}
            open={visible}
            onCancel={onClose}
            closable={false}
            width={600}
            footer={
                shareUrl ? (
                    <Space>
                        <Button onClick={onClose}>{t('share_dialog.close')}</Button>
                    </Space>
                ) : (
                    <Space>
                        <Button onClick={onClose}>{t('share_dialog.cancel')}</Button>
                        <Button
                            type="primary"
                            onClick={handleShare}
                            loading={submitting}
                            disabled={selectedUsernames.length === 0}
                        >
                            {t('share_dialog.generate_link')}
                        </Button>
                    </Space>
                )
            }
        >
            {shareResults.length > 0 ? (
                <div className="space-y-4">
                    {shareUrl&&shareUrl != '' && (
                        <div>
                            <Text strong>{t('share_dialog.share_link')}</Text>
                            <Paragraph
                                copyable={{
                                    text: shareUrl,
                                    icon: [<CopyOutlined key="copy" />, <CheckOutlined key="copied" />],
                                }}
                                className="mt-2 p-3 bg-gray-50 dark:bg-[#1E1E1E] rounded border"
                            >
                                {shareUrl}
                            </Paragraph>
                        </div>
                    )}

                    {shareResults.some(r => !r.success) && (
                        <div>
                            <Text strong className="mb-2 block text-red-600 dark:text-[#F48771]">{t('share_dialog.failed_shares')}</Text>
                            <div className="space-y-1">
                                {shareResults.filter(r => !r.success).map((result, index) => (
                                    <div key={index} className="flex items-start gap-2 p-2 bg-red-50 dark:bg-[#3C1F1F] border border-red-200 dark:border-[#5C2D2D] rounded text-sm">
                                        <CloseCircleOutlined className="text-red-500 dark:text-[#F48771] mt-0.5 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <span className="font-medium text-gray-900 dark:text-[#CCCCCC]">{result.target_user}</span>
                                            <span className="text-gray-600 dark:text-[#999999] ml-2">- {result.message}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    {/* Backup Option */}
                    <div className="p-3 bg-blue-50 dark:bg-[#1E3A5F] rounded border border-blue-200 dark:border-[#0E639C]">
                        {/* <Checkbox
                            checked={withBackup}
                            onChange={(e) => setWithBackup(e.target.checked)}
                        >
                            <span className="text-sm">
                                {t('share_dialog.create_backup')}
                                <span className="text-gray-500 dark:text-[#999999] ml-2">
                                    {t('share_dialog.backup_hint')}
                                </span>
                            </span>
                        </Checkbox> */}
                        <div className='flex w-full'>
                            <Radio.Group
                                onChange={e => setPermissionsType(e.target.value)}
                                value={permissionsType}
                                options={[
                                    { value: 'read', label: t('share_dialog.read_only') },
                                    { value: 'write', label: t('share_dialog.read_write') },
                                ]}
                            />
                        </div>
                    </div>

                    <Select
                        mode="tags"
                        style={{ width: '100%' }}
                        placeholder={t('share_dialog.search_placeholder')}
                        value={selectedUsernames}
                        onChange={(values) => setSelectedUsernames(values)}
                        tokenSeparators={[',', ' ', ';']}
                        open={false}
                        suffixIcon={null}
                    />
                    <div className="text-xs text-gray-500 dark:text-[#999999]">
                        {t('share_dialog.input_hint')}
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default ShareDialog;
